import type { Object3D as ThreeObject3D } from 'three';
import type { Object3D } from 'three/webgpu';
import type { ComponentInstanceTransformTargetResolverHost } from '../../../types';
export type { ComponentInstanceTransformTargetResolverHost } from '../../../types';
import {
  areAssemblyNodeIdentityEquivalent,
  collectAssemblyNodeIdentityLookupIds,
} from '../utils/AssemblyNodePath';
import { isImportedTopologyProxy } from '../../imported-model/selection/imported-topology-proxy-metadata';
import {
  resolveImportedModelTopologyRepository,
  type ImportedModelTopologyRepository,
} from '../../imported-model/topology/ImportedModelTopologyRepository';
import { isImportedModelTopologyHost } from '../../imported-model/topology/imported-model-topology-runtime';
import { ImportedModelObjectMetadataService } from '../../imported-model/metadata';
import { resolveImportedBodyOccurrenceNodeId } from '../../imported-model/utils';
import {
  FeatureResultMesh,
  isFeatureFace,
  isFeatureLine,
  isFeaturePoint,
} from '../../mesh/feature';
import {
  readComponentInstanceAssemblyNodeId,
  resolveComponentInstanceTransformRoot,
  type ResolveComponentInstanceTransformRootOptions,
} from './resolveComponentInstanceTransformRoot';
import {
  hasComponentInstanceImportedModelMetadata,
  readComponentInstanceBooleanValue,
  readComponentInstanceGlbHierarchyMeta,
  readComponentInstanceImportedModelHierarchyMeta,
  readComponentInstanceImportedModelMetadata,
  readComponentInstanceStringValue,
  isInsideSkippedComponentInstanceTraversalSubtree,
  shouldSkipComponentInstanceTraversalSubtree,
} from './utils/ComponentInstanceMetadata';

/**
 * 读取对象声明的选择代理对象 ID。
 *
 * @param object 场景对象。
 * @returns 选择代理对象 ID；不存在时返回空字符串。
 */
function readSelectionProxyObjectId(object: Object3D): string {
  return readComponentInstanceStringValue(
    readComponentInstanceImportedModelMetadata(object),
    'selectionProxyObjectId',
  ) || readComponentInstanceStringValue(
    object.userData,
    'importedSelectionProxyObjectId',
  );
}

/**
 * 读取对象语义类型。
 *
 * @param object 场景对象。
 * @returns 语义类型；不存在时返回空字符串。
 */
function readSemanticKind(object: Object3D): string {
  return readComponentInstanceStringValue(
    readComponentInstanceImportedModelHierarchyMeta(object),
    'semanticKind',
  ) || readComponentInstanceStringValue(
    readComponentInstanceGlbHierarchyMeta(object),
    'semanticKind',
  );
}

/**
 * 判断对象是否声明了直接几何。
 *
 * @param object 场景对象。
 * @returns 是否为直接几何节点。
 */
function hasDirectGeometry(object: Object3D): boolean {
  return readComponentInstanceBooleanValue(
    readComponentInstanceImportedModelHierarchyMeta(object),
    'hasDirectGeometry',
  ) || readComponentInstanceBooleanValue(
    readComponentInstanceGlbHierarchyMeta(object),
    'hasDirectGeometry',
  );
}

/**
 * 判断对象是否为可渲染叶子对象。
 *
 * @param object 场景对象。
 * @returns 是否为 Mesh / Line / Sprite 等几何承载节点。
 */
function isRenderableLeafObject(object: Object3D): boolean {
  return object.type === 'Mesh'
    || object.type === 'Line'
    || object.type === 'LineSegments'
    || object.type === 'Sprite'
    || object.type === 'Points';
}

/**
 * 判断对象是否为 imported-model 的可渲染叶子。
 *
 * @param object 待判断对象。
 * @returns 是否为导入模型的可渲染几何叶子。
 */
function isImportedRenderableLeafObject(object: Object3D): boolean {
  return hasComponentInstanceImportedModelMetadata(object) && isRenderableLeafObject(object);
}

/**
 * 判断当前对象是否被导入器声明为权威组件变换根。
 *
 * 设计说明：
 * complete-glb 保留 GLTFLoader 原始场景树，单 primitive glTF node 会直接由 Mesh 承载。
 * 此时 Three.js 对象类型不能再决定它是否只是渲染叶子；只有对象自身 metadata 同时携带
 * occurrence 身份和导入器写入的权威标记，才允许作为组件位姿写入目标。父级标记不得被
 * 子 Mesh 继承，否则多 primitive 零件会重新退化为局部网格移动。
 *
 * @param object 待判断的运行时场景对象。
 * @returns 当前对象是否为权威组件变换根。
 */
function isAuthoritativeComponentTransformRoot(object: Object3D): boolean {
  const metadata = ImportedModelObjectMetadataService.peekOwnMetadata(
    object as unknown as ThreeObject3D,
  );
  return metadata?.componentTransformRoot === true
    && Boolean(readComponentInstanceAssemblyNodeId(object));
}

/**
 * 判断 imported-model 可渲染叶子是否仍应作为变换代理排除。
 *
 * @param object 待判断对象。
 * @returns 普通渲染叶子返回 true；权威 occurrence Mesh 返回 false。
 */
function isImportedRenderableLeafTransformProxy(object: Object3D): boolean {
  return isImportedRenderableLeafObject(object)
    && !isAuthoritativeComponentTransformRoot(object);
}

/**
 * 判断 imported-model 对象是否不能承载组件 transform。
 *
 * 设计说明：
 * 可见 mesh、body host 和继承错误 selectionProxyObjectId 的对象经常与真实组件根共享
 * assemblyNodeId。它们可以参与选择和高亮，但不能写组件位姿，否则 Mate 预览会移动蓝色面代理，
 * 真实零件仍留在原地。
 *
 * @param object 待判断对象。
 * @returns 是否应被组件 transform 根解析拒绝。
 */
function isImportedModelNonTransformObject(object: Object3D): boolean {
  if (!hasComponentInstanceImportedModelMetadata(object)) {
    return false;
  }

  if (isAuthoritativeComponentTransformRoot(object)) {
    return false;
  }

  const semanticKind = readSemanticKind(object);
  const isSemanticComponentRoot = (semanticKind === 'assembly' || semanticKind === 'part')
    && !isRenderableLeafObject(object);
  const selectionProxyObjectId = readSelectionProxyObjectId(object);
  if (selectionProxyObjectId && selectionProxyObjectId !== object.uuid && !isSemanticComponentRoot) {
    return true;
  }

  if (semanticKind === 'body' || semanticKind === 'body-folder') {
    return true;
  }

  if (isRenderableLeafObject(object) && semanticKind !== 'assembly' && semanticKind !== 'part') {
    /**
     * 旧导入数据里可见 mesh 可能只有 assemblyNodeId，没有完整 hierarchy.meta。
     * 这种 mesh 仍然是几何叶子，不是组件根；真实组件根应通过同 assemblyNodeId 的场景回退寻找。
     */
    return true;
  }

  return hasDirectGeometry(object)
    && semanticKind !== 'assembly'
    && semanticKind !== 'part';
}

/**
 * 判断对象是否为不能承载组件 transform 的代理或叶子对象。
 *
 * @param object 待判断对象。
 * @returns 是否应被组件 transform 根解析拒绝。
 */
export function isComponentInstanceTransformProxyObject(object: Object3D | null | undefined): boolean {
  if (!object) {
    return false;
  }

  return isImportedModelTopologyHost(object)
    || isImportedTopologyProxy(object)
    || isFeatureFace(object)
    || isFeatureLine(object)
    || isFeaturePoint(object)
    || isImportedModelNonTransformObject(object);
}

/**
 * 判断对象是否属于拓扑交互代理链路，供变换解析与组件索引成员分类共同使用。
 *
 * @param object 待判断对象。
 * @returns 是否应优先通过 topology 仓库反查组件作用域根。
 */
export function isImportedTopologyInteractionObject(object: Object3D): boolean {
  return isImportedModelTopologyHost(object)
    || isImportedTopologyProxy(object)
    || isFeatureFace(object)
    || isFeatureLine(object)
    || isFeaturePoint(object);
}

/**
 * 判断两个 assemblyNodeId 是否表示同一组件实例身份。
 *
 * @param left 左侧 assemblyNodeId。
 * @param right 右侧 assemblyNodeId。
 * @returns 两者在装配路径规则下等价时返回 true。
 */
function isSameAssemblyNodeIdentity(left: string | null | undefined, right: string | null | undefined): boolean {
  return areAssemblyNodeIdentityEquivalent(left, right);
}

/**
 * 读取 imported topology host 上记录的 bodyBindingId。
 *
 * @param object 当前场景对象。
 * @returns host bodyBindingId；缺失时返回空字符串。
 */
function readImportedTopologyHostBodyBindingId(object: Object3D): string {
  const userData = object.userData as { importedBodyBindingId?: unknown } | null | undefined;
  return typeof userData?.importedBodyBindingId === 'string'
    ? userData.importedBodyBindingId.trim()
    : '';
}

/**
 * 从 imported topology host 的 body 记录中解析组件实例节点 ID。
 *
 * @param object 当前场景对象。
 * @param repository imported topology 仓库。
 * @returns body 归属的 assemblyNodeId；无法解析时返回空字符串。
 */
/**
 * 从当前运行态 metadata 读取 imported topology 对象所属的组件实例 ID。
 *
 * @param object 当前场景对象。
 * @returns metadata 声明的 assemblyNodeId；缺失时返回空字符串。
 */
function resolveImportedTopologyObjectAssemblyNodeIdFromMetadata(object: Object3D): string {
  return ImportedModelObjectMetadataService
    .resolveSelectionTargetFromSelfOrAncestors(object as unknown as ThreeObject3D)
    ?.assemblyNodeId
    ?? ImportedModelObjectMetadataService
      .resolveAssemblyNodeIdFromSelfOrAncestors(object as unknown as ThreeObject3D)
    ?? '';
}

/**
 * 判断 metadata 中的组件 ID 是否是 body occurrence 的项目挂载版。
 *
 * 设计说明：
 * `bodyBindingId::occurrence` 来自离线包内部，保存后再作为项目节点挂载时可能缺少外层项目路径。
 * 只有当 metadata ID 明确以该 occurrence 为后缀且自身更长时，才说明它是当前项目实例的更具体身份。
 * 根级污染 metadata 不满足这个条件，因此不会覆盖 bodyBindingId 的归属。
 *
 * @param metadataAssemblyNodeId metadata 声明的组件 ID。
 * @param occurrenceNodeId bodyBindingId 解出的局部 occurrence。
 * @returns metadata 更具体时返回 true。
 */
function isMoreSpecificProjectAssemblyNodeId(
  metadataAssemblyNodeId: string | null | undefined,
  occurrenceNodeId: string | null | undefined,
): boolean {
  const metadataLookupIds = collectAssemblyNodeIdentityLookupIds(metadataAssemblyNodeId);
  const occurrenceLookupIds = collectAssemblyNodeIdentityLookupIds(occurrenceNodeId);
  if (metadataLookupIds.length === 0 || occurrenceLookupIds.length === 0) {
    return false;
  }

  return metadataLookupIds.some((metadataLookupId) => (
    occurrenceLookupIds.some((occurrenceLookupId) => (
      metadataLookupId !== occurrenceLookupId
      && metadataLookupId.endsWith(`/${occurrenceLookupId}`)
    ))
  ));
}

/**
 * 从当前可见命中链路中解析比局部 occurrence 更具体的项目组件 ID。
 *
 * @param object 当前 topology host。
 * @param visibleMesh body 对应的可见 mesh。
 * @param occurrenceNodeId bodyBindingId 解出的局部 occurrence。
 * @returns 更具体的项目 occurrence；不存在时返回空字符串。
 */
function resolveMoreSpecificImportedTopologyAssemblyNodeId(
  object: Object3D,
  visibleMesh: Object3D,
  occurrenceNodeId: string,
): string {
  const candidates = [
    resolveImportedTopologyObjectAssemblyNodeIdFromMetadata(object),
    resolveImportedTopologyObjectAssemblyNodeIdFromMetadata(visibleMesh),
    readComponentInstanceAssemblyNodeId(visibleMesh),
  ].filter((candidate) => candidate.length > 0);

  return candidates.find((candidate) => (
    isMoreSpecificProjectAssemblyNodeId(candidate, occurrenceNodeId)
  )) ?? '';
}

function resolveImportedTopologyHostRecordAssemblyNodeId(
  object: Object3D,
  repository: ImportedModelTopologyRepository,
): string {
  if (!(object instanceof FeatureResultMesh) || !isImportedModelTopologyHost(object)) {
    return '';
  }

  const bodyRecord = repository.resolveBodyRecordFromPickHost(object);
  if (!bodyRecord) {
    return '';
  }

  const occurrenceNodeId = resolveImportedBodyOccurrenceNodeId(bodyRecord.bodyBindingId)
    || resolveImportedBodyOccurrenceNodeId(readImportedTopologyHostBodyBindingId(object));
  if (occurrenceNodeId) {
    const projectScopedAssemblyNodeId = resolveMoreSpecificImportedTopologyAssemblyNodeId(
      object,
      bodyRecord.visibleMesh as unknown as Object3D,
      occurrenceNodeId,
    );
    if (projectScopedAssemblyNodeId) {
      return projectScopedAssemblyNodeId;
    }

    /**
     * 面/边/点命中的 transform 写入目标必须以 body occurrence 为准。
     * metadata 可能在离线包保存重载后被根级 selection proxy 污染，而 occurrence 是导出拓扑资产
     * 与当前可见 body 的精确绑定身份，优先级必须高于对象级旧 metadata。
     */
    return occurrenceNodeId;
  }

  return '';
}

/**
 * 判断 topology 父链上的 assemblyNodeId 是否可以作为无 occurrence 时的本地写入身份。
 *
 * 设计说明：
 * repository root 有两种角色：零件根自身可以承载 transform；装配/包根只是拓扑仓库容器。
 * 当 bodyBindingId 没有 occurrence 时，只能接受零件根或普通父级组件根，不能把装配包根当作 body owner。
 *
 * @param object 当前父链对象。
 * @param repositoryRoot imported topology 仓库根。
 * @returns 可以作为当前 body transform 身份时返回 true。
 */
function canUseImportedTopologyAncestorAssemblyNodeId(
  object: Object3D,
  repositoryRoot: Object3D | null,
): boolean {
  if (object !== repositoryRoot) {
    return true;
  }

  const semanticKind = readComponentInstanceStringValue(
    readComponentInstanceImportedModelHierarchyMeta(object),
    'semanticKind',
  );
  return semanticKind === 'part';
}

/**
 * 从命中的 topology 交互对象父链解析当前 body 的组件实例节点 ID。
 *
 * 设计说明：
 * FeatureFace/Line/Point 自身通常没有 imported-model metadata，但它们挂在 pick host 下。
 * pick host 的 bodyBindingId 是离线包保存的 body occurrence 权威身份，必须优先于 repository root，
 * 否则 repository 注册在包根时会把子零件拖拽错误写到整棵导入包。
 *
 * @param object 当前命中的 topology 交互对象。
 * @param repository imported topology 仓库。
 * @returns 当前 body 对应的 assemblyNodeId；无法解析时返回空字符串。
 */
function resolveImportedTopologyInteractionAssemblyNodeId(
  object: Object3D,
  repository: ImportedModelTopologyRepository,
): string {
  const repositoryRoot = repository.getRoot() as unknown as Object3D | null;
  let current: Object3D | null = object;
  while (current) {
    const hostRecordNodeId = resolveImportedTopologyHostRecordAssemblyNodeId(current, repository);
    if (hostRecordNodeId) {
      return hostRecordNodeId;
    }

    const currentNodeId = readComponentInstanceAssemblyNodeId(current);
    if (
      currentNodeId
      && canUseImportedTopologyAncestorAssemblyNodeId(current, repositoryRoot)
      && !isComponentInstanceTransformProxyObject(current)
      && !isImportedRenderableLeafTransformProxy(current)
    ) {
      return currentNodeId;
    }

    const occurrenceNodeId = resolveImportedBodyOccurrenceNodeId(readImportedTopologyHostBodyBindingId(current));
    if (occurrenceNodeId) {
      return occurrenceNodeId;
    }

    current = current.parent;
  }

  return '';
}

/**
 * 沿命中对象父链查找与 body occurrence 等价的可写组件根。
 *
 * @param object 当前命中的 topology 交互对象。
 * @param assemblyNodeId body occurrence 对应的 assemblyNodeId。
 * @returns 父链中的可写组件根；无法解析时返回 null。
 */
function resolveAncestorTransformRootByAssemblyNodeId(
  object: Object3D,
  assemblyNodeId: string,
): Object3D | null {
  let current: Object3D | null = object;
  while (current) {
    const currentNodeId = readComponentInstanceAssemblyNodeId(current);
    if (
      currentNodeId
      && isSameAssemblyNodeIdentity(currentNodeId, assemblyNodeId)
      && !isInsideSkippedComponentInstanceTraversalSubtree(current)
      && !isComponentInstanceTransformProxyObject(current)
      && !isImportedRenderableLeafTransformProxy(current)
    ) {
      return current;
    }

    current = current.parent;
  }

  return null;
}

/**
 * 通过 imported topology 仓库解析可写 transform 根。
 *
 * 设计说明：
 * FeatureFace/Line/Point 与 pick host 是按需创建的交互代理，常常携带稳定拓扑引用和 assemblyNodeId，
 * 但它们不是完整零件实例的渲染根。配合对齐切换会产生旋转，如果写到代理本身，就会出现拓扑高亮已经
 * 旋转而真实实体 mesh 留在原位的分离。仓库根是创建 pick host 时的作用域根，因此是这条链路里唯一
 * 能同时覆盖可见 mesh 与拓扑代理的稳定写入目标。
 *
 * 注意：`bodyBindingSelectionTargetsById` 只表示 Mate/基准等拓扑引用归属，不表示 Move/Rotate
 * 的 transform 写入目标。命中面拖拽时必须沿当前 pick host 所在实例解析，否则会把子零件拖拽错误写到
 * Mate 所属父装配根，导致同源装配体整体移动。
 *
 * @param object 当前命中的拓扑代理或 Feature 对象。
 * @param host 解析宿主。
 * @returns 仓库根对应的可写 transform 目标；无法安全解析时返回 null。
 */
function resolveImportedTopologyRepositoryTransformRoot(
  object: Object3D,
  host: ComponentInstanceTransformTargetResolverHost,
): Object3D | null {
  if (!isImportedTopologyInteractionObject(object)) {
    return null;
  }

  const repository = resolveImportedModelTopologyRepository(object as unknown as ThreeObject3D);
  const repositoryRoot = repository?.getRoot() as unknown as Object3D | null;
  if (!repositoryRoot || repositoryRoot === object) {
    return null;
  }

  const assemblyNodeId = resolveImportedTopologyInteractionAssemblyNodeId(object, repository);
  if (assemblyNodeId) {
    const ancestorTransformRoot = resolveAncestorTransformRootByAssemblyNodeId(object, assemblyNodeId);
    if (ancestorTransformRoot) {
      return ancestorTransformRoot;
    }

    const assemblyTransformRoot = findComponentInstanceTransformRootByAssemblyNodeId(host, assemblyNodeId);
    if (assemblyTransformRoot && !isComponentInstanceTransformProxyObject(assemblyTransformRoot)) {
      /**
       * 外部引用装配会把拓扑仓库注册到 part render root，而真正的组件位姿在外层
       * instance group。对齐切换写旋转时必须优先按 assemblyNodeId 回到最外层可写根，
       * 否则 visible mesh 与 FeatureFace/pick host 会被写到不同 transform 作用域。
       */
      return assemblyTransformRoot;
    }
  }

  /**
   * Move/Rotate 的写入目标必须来自当前命中 body 的 occurrence 或父链组件身份。
   * topology repository root 只是交互资产容器；当 bodyBindingId 缺少 occurrence 时，
   * 回退到包根会把子零件拖拽错误升级为整包/整场景移动。
   */
  return null;
}

/**
 * 解析宿主场景。
 *
 * @param host 解析宿主。
 * @returns 当前场景；缺失时返回 null。
 */
function resolveHostScene(host: ComponentInstanceTransformTargetResolverHost): Object3D | null {
  return host.getScene?.() ?? host.scene ?? null;
}

/**
 * 访问同装配身份候选；批量解析复用一次建好的桶，不为每个叶子重新遍历场景。
 *
 * @param host 候选查询宿主。
 * @param root 当前场景根。
 * @param assemblyNodeId 原始装配身份，保持既有精确匹配语义。
 * @param visitor 访问函数。
 */
function traverseComponentInstanceTransformObjects(
  host: ComponentInstanceTransformTargetResolverHost,
  root: Object3D,
  assemblyNodeId: string,
  visitor: (object: Object3D) => void,
): void {
  if (host.forEachAssemblyNodeCandidate) {
    host.forEachAssemblyNodeCandidate(assemblyNodeId, visitor);
    return;
  }
  // 单对象调用方未提供索引时，仍支持直接传入普通 Three.js 场景。
  const visit = (object: Object3D): void => {
    if (object !== root && shouldSkipComponentInstanceTraversalSubtree(object)) {
      return;
    }

    visitor(object);
    object.children.forEach((child) => {
      visit(child as Object3D);
    });
  };

  visit(root);
}

/**
 * 在同一 assemblyNodeId 的父链中找到最高候选节点。
 *
 * @param object 当前命中对象。
 * @param assemblyNodeId 目标装配节点 ID。
 * @returns 父链中最高的同装配节点对象。
 */
function resolveHighestSameAssemblyNodeAncestor(
  object: Object3D,
  assemblyNodeId: string,
): Object3D {
  let current: Object3D | null = object;
  let highestSameAssemblyNodeObject: Object3D = object;

  while (current) {
    if (readComponentInstanceAssemblyNodeId(current) !== assemblyNodeId) {
      break;
    }
    highestSameAssemblyNodeObject = current;
    current = current.parent;
  }

  return highestSameAssemblyNodeObject;
}

/**
 * 创建组件 transform 根解析选项。
 *
 * @param host 解析宿主。
 * @param enableAssemblyNodeFallback 是否启用 assemblyNodeId 场景回退。
 * @returns 通用组件根解析器选项。
 */
function createResolveOptions(
  host: ComponentInstanceTransformTargetResolverHost,
  enableAssemblyNodeFallback: boolean,
): ResolveComponentInstanceTransformRootOptions {
  return {
    isRejectedTransformRoot: isComponentInstanceTransformProxyObject,
    ...(host.getSceneObjectById
      ? { getSceneObjectById: (objectId: string) => host.getSceneObjectById?.(objectId) ?? null }
      : {}),
    ...(enableAssemblyNodeFallback
      ? {
          findTransformRootByAssemblyNodeId: (assemblyNodeId: string) => (
            findComponentInstanceTransformRootByAssemblyNodeId(host, assemblyNodeId)
          ),
        }
      : {}),
  };
}

/**
 * 按 assemblyNodeId 在场景中查找真实可写组件根。
 *
 * @param host 解析宿主。
 * @param assemblyNodeId 装配实例节点 ID。
 * @returns 可写组件根；找不到时返回 null。
 */
function findComponentInstanceTransformRootByAssemblyNodeId(
  host: ComponentInstanceTransformTargetResolverHost,
  assemblyNodeId: string,
): Object3D | null {
  const scene = resolveHostScene(host);
  if (!scene || !assemblyNodeId) {
    return null;
  }

  let matchedObject: Object3D | null = null;
  const resolveOptions = createResolveOptions(host, false);
  traverseComponentInstanceTransformObjects(host, scene, assemblyNodeId, (object) => {
    if (matchedObject || readComponentInstanceAssemblyNodeId(object) !== assemblyNodeId) {
      return;
    }

    const highestCandidate = resolveHighestSameAssemblyNodeAncestor(object, assemblyNodeId);
    const transformRoot = resolveComponentInstanceTransformRoot(highestCandidate, resolveOptions);
    if (!transformRoot || isComponentInstanceTransformProxyObject(transformRoot)) {
      return;
    }
    if (isImportedRenderableLeafTransformProxy(transformRoot)) {
      const preferredRoot = findPreferredNonRenderableTransformRootByAssemblyNodeId(
        host,
        assemblyNodeId,
        transformRoot,
      );
      if (preferredRoot) {
        matchedObject = preferredRoot;
      }
      return;
    }

    matchedObject = transformRoot;
  });

  return matchedObject
    ?? findSharedParentTransformRootByAssemblyNodeId(host, assemblyNodeId);
}

/**
 * 当解析结果落到可见 Mesh/Line 等叶子时，尝试按 assemblyNodeId 找到更稳定的非叶子组件根。
 *
 * 设计说明：
 * 旧 STEP/GLB sidecar 可能把 `semanticKind=part`、`selectionProxyObjectId=self` 写到可见 Mesh 上。
 * 该 Mesh 可用于选择和高亮，但不应承载装配组件 transform；若同一 assemblyNodeId 下存在
 * Group/part 根，必须优先写根节点，避免配合预览或对齐切换只移动部分网格。
 *
 * @param host 解析宿主。
 * @param assemblyNodeId 装配节点 ID。
 * @param excludedObject 当前已经解析到的叶子对象。
 * @returns 更稳定的组件根；没有更优目标时返回 null。
 */
function findPreferredNonRenderableTransformRootByAssemblyNodeId(
  host: ComponentInstanceTransformTargetResolverHost,
  assemblyNodeId: string,
  excludedObject: Object3D,
): Object3D | null {
  const scene = resolveHostScene(host);
  if (!scene || !assemblyNodeId) {
    return null;
  }

  let matchedObject: Object3D | null = null;
  const resolveOptions = createResolveOptions(host, false);
  traverseComponentInstanceTransformObjects(host, scene, assemblyNodeId, (object) => {
    if (matchedObject || object === excludedObject || readComponentInstanceAssemblyNodeId(object) !== assemblyNodeId) {
      return;
    }

    const highestCandidate = resolveHighestSameAssemblyNodeAncestor(object, assemblyNodeId);
    const transformRoot = resolveComponentInstanceTransformRoot(highestCandidate, resolveOptions);
    if (!transformRoot || transformRoot === excludedObject || isComponentInstanceTransformProxyObject(transformRoot)) {
      return;
    }
    if (isImportedRenderableLeafTransformProxy(transformRoot)) {
      return;
    }

    matchedObject = transformRoot;
  });

  return matchedObject
    ?? findSharedParentTransformRootByAssemblyNodeId(host, assemblyNodeId, excludedObject);
}

/**
 * 创建对象到场景根的父链。
 *
 * @param object 起始对象。
 * @returns 从场景根到对象自身的父链。
 */
function createAncestorChainFromRoot(object: Object3D): Object3D[] {
  const chain: Object3D[] = [];
  let current: Object3D | null = object;
  while (current) {
    chain.unshift(current);
    current = current.parent;
  }
  return chain;
}

/**
 * 解析多个对象的最低共同父节点。
 *
 * @param objects 对象集合。
 * @returns 最低共同父节点；无法解析时返回 null。
 */
function resolveLowestCommonAncestor(objects: readonly Object3D[]): Object3D | null {
  if (objects.length === 0) {
    return null;
  }

  const chains = objects.map(createAncestorChainFromRoot);
  let commonAncestor: Object3D | null = null;
  const shortestChainLength = Math.min(...chains.map((chain) => chain.length));

  for (let chainIndex = 0; chainIndex < shortestChainLength; chainIndex += 1) {
    const candidate = chains[0]?.[chainIndex] ?? null;
    if (!candidate || !chains.every((chain) => chain[chainIndex] === candidate)) {
      break;
    }
    commonAncestor = candidate;
  }

  return commonAncestor;
}

/**
 * 判断对象是否为场景根或已经脱离场景。
 *
 * @param object 待判断对象。
 * @param scene 当前场景。
 * @returns 是否不能作为组件 transform 根。
 */
function isInvalidSharedTransformRoot(object: Object3D | null, scene: Object3D): boolean {
  return !object || object === scene || !object.parent;
}

/**
 * 按 assemblyNodeId 查找多个可见网格的共同组件父根。
 *
 * 设计说明：
 * 旧 STEP/GLB sidecar 可能只在多个兄弟 Mesh 上写 assemblyNodeId，父 Group 没有任何语义元数据。
 * 这类数据如果把 transform 写到单个 Mesh，会导致同一零件的其它 Mesh 留在原地；
 * 因此在没有显式语义根时，用多个同实例可渲染叶子的最低共同父节点作为组件根。
 *
 * @param host 解析宿主。
 * @param assemblyNodeId 装配实例节点 ID。
 * @param excludedObject 当前已知不可取的叶子对象。
 * @returns 共同父根；无法安全解析时返回 null。
 */
function findSharedParentTransformRootByAssemblyNodeId(
  host: ComponentInstanceTransformTargetResolverHost,
  assemblyNodeId: string,
  excludedObject?: Object3D,
): Object3D | null {
  const scene = resolveHostScene(host);
  if (!scene || !assemblyNodeId) {
    return null;
  }

  const renderableLeaves: Object3D[] = [];
  traverseComponentInstanceTransformObjects(host, scene, assemblyNodeId, (object) => {
      if (
        object === excludedObject
        || readComponentInstanceAssemblyNodeId(object) !== assemblyNodeId
        || !isImportedRenderableLeafTransformProxy(object)
    ) {
      return;
    }
    renderableLeaves.push(object);
  });

  if (excludedObject && isImportedRenderableLeafTransformProxy(excludedObject)) {
    renderableLeaves.push(excludedObject);
  }

  if (renderableLeaves.length < 2) {
    return null;
  }

  const sharedRoot = resolveLowestCommonAncestor(renderableLeaves);
  if (isInvalidSharedTransformRoot(sharedRoot, scene) || isComponentInstanceTransformProxyObject(sharedRoot)) {
    return null;
  }

  return sharedRoot;
}

/**
 * 解析组件实例真正应写入 transform 的对象。
 *
 * @param object 当前选中、命中或按组件 ID 查询到的场景对象。
 * @param host 解析宿主。
 * @returns 可写组件根；无有效目标时返回 null。
 */
export function resolveComponentInstanceTransformTarget(
  object: Object3D | null | undefined,
  host: ComponentInstanceTransformTargetResolverHost,
): Object3D | null {
  if (!object) {
    return null;
  }

  if (isImportedTopologyInteractionObject(object)) {
    const importedTopologyRoot = resolveImportedTopologyRepositoryTransformRoot(object, host);
    if (importedTopologyRoot) {
      return importedTopologyRoot;
    }
    if (resolveImportedModelTopologyRepository(object as unknown as ThreeObject3D)) {
      // repository 已证明这是拓扑交互对象；无法精确落到 occurrence 时必须中止，避免回退到包根或共同父根。
      return null;
    }
  }
  if (isInsideSkippedComponentInstanceTraversalSubtree(object)) {
    return null;
  }

  const transformRoot = resolveComponentInstanceTransformRoot(
    object,
    createResolveOptions(host, true),
  );
  if (!transformRoot || !isImportedRenderableLeafTransformProxy(transformRoot)) {
    return transformRoot;
  }

  const assemblyNodeId = readComponentInstanceAssemblyNodeId(transformRoot);
  return findPreferredNonRenderableTransformRootByAssemblyNodeId(host, assemblyNodeId, transformRoot)
    ?? transformRoot;
}
